const ACCENT_COLOR_CACHE = new Map();

const FALLBACK_ACCENT = {
  accent: 'rgb(255, 181, 0)',
  hoverBackground: 'rgba(255, 181, 0, 0.18)',
  hoverOutline: 'rgba(255, 181, 0, 0.22)',
};

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function rgbToHsl(r, g, b) {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;

  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));

    switch (max) {
    case red:
      h = ((green - blue) / delta) % 6;
      break;
    case green:
      h = (blue - red) / delta + 2;
      break;
    default:
      h = (red - green) / delta + 4;
      break;
    }

    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s, l };
}

function hslToRgb(h, s, l) {
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const normalizedHue = h / 60;
  const x = chroma * (1 - Math.abs((normalizedHue % 2) - 1));

  let red = 0;
  let green = 0;
  let blue = 0;

  if (normalizedHue >= 0 && normalizedHue < 1) {
    red = chroma;
    green = x;
  } else if (normalizedHue < 2) {
    red = x;
    green = chroma;
  } else if (normalizedHue < 3) {
    green = chroma;
    blue = x;
  } else if (normalizedHue < 4) {
    green = x;
    blue = chroma;
  } else if (normalizedHue < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  const match = l - chroma / 2;

  return {
    r: Math.round((red + match) * 255),
    g: Math.round((green + match) * 255),
    b: Math.round((blue + match) * 255),
  };
}

function normalizeAccentColor(r, g, b) {
  const { h, s, l } = rgbToHsl(r, g, b);
  const normalized = hslToRgb(
    h,
    clamp(s, 0.22, 0.58),
    clamp(l, 0.3, 0.62)
  );

  return normalized;
}

function buildAccentPayload({ r, g, b }) {
  return {
    accent: `rgb(${r}, ${g}, ${b})`,
    hoverBackground: `rgba(${r}, ${g}, ${b}, 0.18)`,
    hoverOutline: `rgba(${r}, ${g}, ${b}, 0.22)`,
  };
}

function extractWeightedAverageColor(imageData) {
  const { data } = imageData;
  let totalWeight = 0;
  let red = 0;
  let green = 0;
  let blue = 0;

  for (let index = 0; index < data.length; index += 4) {
    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const alpha = data[index + 3] / 255;

    if (alpha < 0.35) {
      continue;
    }

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    if (luminance < 0.06 || luminance > 0.96) {
      continue;
    }

    const weight = alpha * (0.35 + saturation * 1.65);

    red += r * weight;
    green += g * weight;
    blue += b * weight;
    totalWeight += weight;
  }

  if (totalWeight === 0) {
    return null;
  }

  return {
    r: Math.round(red / totalWeight),
    g: Math.round(green / totalWeight),
    b: Math.round(blue / totalWeight),
  };
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

export async function extractImageAccentColor(source) {
  if (!source) {
    return FALLBACK_ACCENT;
  }

  if (ACCENT_COLOR_CACHE.has(source)) {
    return ACCENT_COLOR_CACHE.get(source);
  }

  try {
    const image = await loadImage(source);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (!context) {
      return FALLBACK_ACCENT;
    }

    const sampleWidth = 24;
    const sampleHeight = 24;

    canvas.width = sampleWidth;
    canvas.height = sampleHeight;
    context.drawImage(image, 0, 0, sampleWidth, sampleHeight);

    const imageData = context.getImageData(0, 0, sampleWidth, sampleHeight);
    const averageColor = extractWeightedAverageColor(imageData);

    if (!averageColor) {
      ACCENT_COLOR_CACHE.set(source, FALLBACK_ACCENT);
      return FALLBACK_ACCENT;
    }

    const normalized = normalizeAccentColor(
      averageColor.r,
      averageColor.g,
      averageColor.b
    );
    const accentPayload = buildAccentPayload(normalized);

    ACCENT_COLOR_CACHE.set(source, accentPayload);
    return accentPayload;
  } catch (error) {
    ACCENT_COLOR_CACHE.set(source, FALLBACK_ACCENT);
    return FALLBACK_ACCENT;
  }
}

export function getFallbackImageAccentColor() {
  return FALLBACK_ACCENT;
}
